import type {
  IWorkJourneyMatrixCell,
  IWorkJourneyMatrixDayCoverage,
  IWorkJourneyMatrixUser,
  TWorkJourneyMatrixCellKind,
} from '@src/models/interfaces/workJourneyManagement.interface';

import { isHolidayCell } from './matrixCellDisplay';
import { isMatrixScheduledWorkDay } from './matrixRestDays';

/**
 * Kinds that remove a plantilla seat from Cobertura/Mínimo unless a REM covers
 * that member. Mirrors Services `COVERAGE_DRAIN_KINDS`.
 */
const COVERAGE_DRAIN_KINDS: ReadonlySet<TWorkJourneyMatrixCellKind> = new Set<TWorkJourneyMatrixCellKind>([
  'justified_absence',
  'unjustified_absence',
  'vacation',
  'medical_leave',
  'paid_leave',
  'unpaid_leave',
  'prenatal',
  'postnatal',
  'other_absence',
  'replacement_out',
]);

export const isCoverageDrainKind = (kind: TWorkJourneyMatrixCellKind): boolean =>
  COVERAGE_DRAIN_KINDS.has(kind);

export type MatrixCoverageCell = Pick<
  IWorkJourneyMatrixCell,
  'userId' | 'kind' | 'code' | 'replacedUserId'
>;

/**
 * Working headcount for one calendar day:
 * `assignedMembers − uncovered drains`. An inbound REM covering a plantilla
 * member cancels that drain and does not add an extra seat.
 * Mirrors Services `computeMatrixDayWorkingCount`.
 */
export const computeMatrixDayWorkingCount = (
  assignedMemberIds: readonly string[],
  dayCells: readonly MatrixCoverageCell[],
): number => {
  const memberIds = new Set(assignedMemberIds.filter(Boolean));
  if (memberIds.size === 0) {
    return 0;
  }

  const coveredMemberIds = new Set<string>();
  dayCells.forEach(cell => {
    const isRem = cell.kind === 'replacement' || cell.code === 'REM';
    if (!isRem || !cell.replacedUserId) {
      return;
    }
    if (memberIds.has(cell.replacedUserId)) {
      coveredMemberIds.add(cell.replacedUserId);
    }
  });

  let working = memberIds.size;
  const drainedMemberIds = new Set<string>();
  dayCells.forEach(cell => {
    if (!memberIds.has(cell.userId) || drainedMemberIds.has(cell.userId)) {
      return;
    }
    if (!isCoverageDrainKind(cell.kind)) {
      return;
    }
    if (coveredMemberIds.has(cell.userId)) {
      return;
    }
    drainedMemberIds.add(cell.userId);
    working -= 1;
  });

  return Math.max(0, working);
};

export type RecomputeMatrixDayCoverageParams = {
  days: readonly string[];
  cells: readonly IWorkJourneyMatrixCell[];
  users: readonly IWorkJourneyMatrixUser[];
  requiredMembers: number;
  daysOfWeek?: Array<string | number> | null;
  /** ISO dates from listMonthMatrix / HolidayService (preferred). */
  holidayDates?: readonly string[] | null;
};

/**
 * Rebuilds the matrix coverage row from the current cells so optimistic
 * exception/attendance patches keep `working/required` in sync without a reload.
 * Public holidays and rest days: nobody is expected → `0/0` green
 * (`required: 0`, `belowMinimum: false`). Mirrors Services
 * `buildMatrixDayCoverageEntry`.
 */
export const recomputeMatrixDayCoverage = ({
  days,
  cells,
  users,
  requiredMembers,
  daysOfWeek,
  holidayDates,
}: RecomputeMatrixDayCoverageParams): IWorkJourneyMatrixDayCoverage[] => {
  const requiredMembersSafe = Math.max(0, requiredMembers || 0);
  // Plantilla only — cover-only rows set `isMember: false` and must not inflate working.
  const assignedMemberIds = users.filter(user => user.isMember).map(user => user.id);
  const holidayDateSet = new Set((holidayDates ?? []).filter(Boolean));

  return days.map(date => {
    const isWorkDay = isMatrixScheduledWorkDay(date, daysOfWeek);
    const dayCells = cells.filter(cell => cell.date === date);
    const isHoliday =
      holidayDateSet.has(date) ||
      dayCells.some(cell => assignedMemberIds.includes(cell.userId) && isHolidayCell(cell));
    const countsForMinimum = isWorkDay && !isHoliday;
    const required = countsForMinimum ? requiredMembersSafe : 0;
    const working = countsForMinimum
      ? computeMatrixDayWorkingCount(assignedMemberIds, dayCells)
      : 0;
    return {
      date,
      working,
      required,
      belowMinimum: countsForMinimum && required > 0 && working < required,
    };
  });
};
